/**
 * Growth paths + daily suggested goals.
 *
 * 9 paths × 10 prompts each = a quiet rotation of trauma-informed daily
 * nudges. Picked deterministically by day-of-year so the same date always
 * shows the same suggestion (no jarring re-renders, no analytics needed).
 *
 * Spanish drafts marked `// review pending` for the trauma-informed editor.
 */

const PATHS = [
  {
    slug: "boundaries",
    icon: "shield",
    label_en: "Boundaries",
    label_es: "Límites",
    desc_en: "Soft, clear, and yours.",
    desc_es: "Suaves, claros y tuyos.",
    prompts_en: [
      "Say one no today — without explaining why.",
      "Notice one moment you wanted to over-explain. Pause instead.",
      "Decline one favor that drains you. Keep it short.",
      "Leave one text on read for an hour before answering.",
      "Choose one room or hour today that belongs only to you.",
      "When you feel guilt, ask: ‘would I want my sister to do this?’",
      "Practice the sentence: ‘That doesn’t work for me.’",
      "Set one tiny boundary that costs nothing — a phone face-down at dinner.",
      "Notice once when ‘maybe’ would have been a no. Honor that next time.",
      "End one conversation when you’re ready, not when they are.",
    ],
    prompts_es: [
      "Di un no hoy — sin explicar por qué.",
      "Nota un momento en que quisiste sobreexplicar. Haz una pausa.",
      "Rechaza un favor que te agota. Sé breve.",
      "Deja un mensaje en visto por una hora antes de responder.",
      "Elige hoy una habitación o una hora que sea solo tuya.",
      "Cuando aparezca la culpa, pregúntate: ‘¿querría esto para mi hermana?’",
      "Practica la frase: ‘Eso no me funciona.’",
      "Pon un límite pequeñísimo que no cueste nada — el celular boca abajo durante la cena.",
      "Nota una vez en que un ‘tal vez’ era en realidad un no. Hónralo la próxima vez.",
      "Termina una conversación cuando tú estés lista, no cuando lo esté la otra persona.",
    ],
  },
  {
    slug: "financial-freedom",
    icon: "wallet",
    label_en: "Financial Freedom",
    label_es: "Libertad Económica",
    desc_en: "Quiet steps toward autonomy.",
    desc_es: "Pasos discretos hacia la autonomía.",
    prompts_en: [
      "Open or check one account that is only in your name.",
      "Move $5 (or whatever feels safe) to a private savings.",
      "Photograph one important document — ID, deed, statement.",
      "List one bill you could pay independently next month.",
      "Note one skill you have that could earn $50.",
      "Check your credit report. Even just glance.",
      "Save one screenshot of an income idea that interests you.",
      "Find one item to sell, list it, even just draft it.",
      "Compare two rental listings in your area — privately.",
      "Write down one financial fact you don’t know yet — and find out.",
    ],
    prompts_es: [
      "Abre o revisa una cuenta que esté solo a tu nombre.",
      "Mueve $5 (o lo que se sienta seguro) a un ahorro privado.",
      "Fotografía un documento importante — ID, escritura, estado de cuenta.",
      "Anota una factura que podrías pagar tú sola el próximo mes.",
      "Nota una habilidad tuya que podría generar $50.",
      "Revisa tu reporte de crédito. Aunque sea un vistazo.",
      "Guarda una captura de una idea de ingreso que te interese.",
      "Encuentra un artículo para vender, ponlo en lista — aunque sea borrador.",
      "Compara dos rentas en tu zona — en privado.",
      "Escribe un dato financiero que aún no conoces — y averígualo.",
    ],
  },
  {
    slug: "nervous-system",
    icon: "leaf",
    label_en: "Nervous System",
    label_es: "Sistema Nervioso",
    desc_en: "Slow is the medicine.",
    desc_es: "La lentitud es la medicina.",
    prompts_en: [
      "Three slow exhales. Longer than your inhales.",
      "Hand on chest, hand on belly. One minute.",
      "Step outside for two minutes of sky.",
      "Drink one glass of water — slowly.",
      "Unclench your jaw three times today.",
      "Walk slower than feels natural for one block.",
      "Notice five neutral things in this room.",
      "Cool water on your wrists for thirty seconds.",
      "Lights off one hour before bed tonight.",
      "Press your feet into the floor for ten seconds.",
    ],
    prompts_es: [
      "Tres exhalaciones lentas. Más largas que las inhalaciones.",
      "Una mano en el pecho, otra en el vientre. Un minuto.",
      "Sal dos minutos a ver el cielo.",
      "Bebe un vaso de agua — despacio.",
      "Suelta la mandíbula tres veces hoy.",
      "Camina más lento de lo natural por una cuadra.",
      "Nota cinco cosas neutras en este cuarto.",
      "Agua fría en las muñecas por treinta segundos.",
      "Apaga las luces una hora antes de dormir.",
      "Presiona los pies contra el piso por diez segundos.",
    ],
  },
  {
    slug: "recovery",
    icon: "heart",
    label_en: "Recovery",
    label_es: "Recuperación",
    desc_en: "Healing is rarely linear.",
    desc_es: "Sanar rara vez es lineal.",
    prompts_en: [
      "Name one thing your body did well today.",
      "Eat one warm meal slowly.",
      "Take a nap without guilt.",
      "Block one account that still pulls you backward.",
      "Notice one moment of mild peace and stay there.",
      "Write a sentence about what you wish someone had said.",
      "Choose softness over performance today.",
      "Cry if you need to. No timer.",
      "Move your body for five gentle minutes.",
      "Forgive yourself for one thing — even quietly.",
    ],
    prompts_es: [
      "Nombra una cosa que tu cuerpo hizo bien hoy.",
      "Come una comida caliente despacio.",
      "Toma una siesta sin culpa.",
      "Bloquea una cuenta que aún te jale hacia atrás.",
      "Nota un momento de calma leve y quédate ahí.",
      "Escribe una frase sobre lo que ojalá alguien te hubiera dicho.",
      "Hoy elige suavidad antes que rendimiento.",
      "Llora si lo necesitas. Sin reloj.",
      "Mueve tu cuerpo cinco minutos suaves.",
      "Perdónate una cosa — aunque sea en silencio.",
    ],
  },
  {
    slug: "self-trust",
    icon: "compass",
    label_en: "Self Trust",
    label_es: "Confianza en Ti Misma",
    desc_en: "Your knowing is a compass.",
    desc_es: "Lo que tú sabes es una brújula.",
    prompts_en: [
      "Write down one thing you noticed but didn’t say out loud.",
      "Trust your first answer today — even once.",
      "Don’t poll three friends for one decision.",
      "Make one small choice based only on your taste.",
      "Listen to one body signal (hunger, tired, full) before someone else’s.",
      "Notice a moment your gut spoke — and write it down.",
      "Disagree silently with someone today without correcting yourself.",
      "Buy or skip one thing without consulting anyone.",
      "Tell yourself: ‘I’m allowed to know what I know.’",
      "Sleep on one decision instead of solving it tonight.",
    ],
    prompts_es: [
      "Escribe algo que notaste pero no dijiste en voz alta.",
      "Confía en tu primera respuesta hoy — aunque sea una vez.",
      "No consultes a tres amigas para una sola decisión.",
      "Toma una decisión pequeña solo por tu gusto.",
      "Escucha una señal de tu cuerpo (hambre, sueño, saciedad) antes que a otra persona.",
      "Nota un momento en que tu intuición habló — y anótalo.",
      "Disiente en silencio hoy sin corregirte.",
      "Compra o salta algo sin consultarle a nadie.",
      "Dite a ti misma: ‘Tengo permiso de saber lo que sé.’",
      "Duerme sobre una decisión en vez de resolverla esta noche.",
    ],
  },
  {
    slug: "parenting",
    icon: "sun",
    label_en: "Parenting",
    label_es: "Crianza",
    desc_en: "Calm in your body becomes calm in theirs.",
    desc_es: "La calma en tu cuerpo se vuelve la calma del suyo.",
    prompts_en: [
      "Three slow breaths before pickup or drop-off.",
      "Repair one small moment from yesterday with five words.",
      "Sit on the floor with them for two minutes — no phone.",
      "Let one ‘messy’ thing be messy today.",
      "Notice one thing they did that delighted you. Tell them.",
      "Hold a boundary kindly. They can handle disappointment.",
      "Eat one meal together — even if it’s cereal.",
      "Apologize, briefly, if you snapped.",
      "Read or play for ten minutes, fully there.",
      "Whisper one truth: ‘I love you in every mood.’",
    ],
    prompts_es: [
      "Tres respiraciones lentas antes de la entrega o la recogida.",
      "Repara un momento pequeño de ayer con cinco palabras.",
      "Siéntate con elle en el piso dos minutos — sin celular.",
      "Deja que una cosa ‘desordenada’ esté desordenada hoy.",
      "Nota algo que hizo que te encantó. Díselo.",
      "Sostén un límite con amabilidad. Puede con la decepción.",
      "Compartan una comida — aunque sea cereal.",
      "Pide perdón, breve, si reaccionaste de más.",
      "Lee o juega diez minutos, totalmente presente.",
      "Susurra una verdad: ‘Te amo en cualquier estado.’",
    ],
  },
  {
    slug: "pet-protection",
    icon: "paw",
    label_en: "Pet Protection",
    label_es: "Protección de Mascotas",
    desc_en: "They count. Make them safe too.",
    desc_es: "Ellos cuentan. Mantenlos a salvo también.",
    prompts_en: [
      "Photograph your pet’s vaccine records today.",
      "Add a trusted person who could foster overnight if needed.",
      "Stash a spare leash or carrier outside the home.",
      "Save one local DV-friendly pet shelter to your phone.",
      "Write down your pet’s vet, microchip number, and dose schedule.",
      "Snap a recent photo of your pet (proof of ownership).",
      "Move pet meds into a small ‘grab bag.’",
      "Save SAF-T (Safe Havens for Pets) link.",
      "Identify one motel within 30 miles that allows pets.",
      "Quietly transfer ownership records into your name only.",
    ],
    prompts_es: [
      "Fotografía hoy las cartillas de vacunas de tu mascota.",
      "Agrega a una persona de confianza que podría cuidarla una noche.",
      "Guarda una correa o transportadora extra fuera de casa.",
      "Guarda en tu celular un refugio local que reciba mascotas en casos de violencia.",
      "Anota su veterinario, número de microchip y horario de medicinas.",
      "Toma una foto reciente (prueba de tenencia).",
      "Mueve las medicinas a una pequeña ‘mochila de salida.’",
      "Guarda el enlace de SAF-T (Safe Havens for Pets).",
      "Identifica un motel pet-friendly a menos de 50 km.",
      "Transfiere en silencio los registros a tu nombre.",
    ],
  },
  {
    slug: "hormones-cycles",
    icon: "moon",
    label_en: "Hormones & Cycles",
    label_es: "Hormonas y Ciclos",
    desc_en: "Your hormones are not your enemy.",
    desc_es: "Tus hormonas no son tu enemiga.",
    prompts_en: [
      "Note where you are in your cycle without judgment.",
      "If luteal: rest harder, expect less.",
      "If ovulating: don’t reopen the closed door today.",
      "Track one body signal (sleep, appetite, mood) in your notes.",
      "Eat one mineral-rich snack — nuts, eggs, greens, dark chocolate.",
      "Cancel one optional plan if your body is asking to.",
      "Move gently — walk over run.",
      "Hydrate, then magnesium, then bed earlier.",
      "Tell one safe person what phase you’re in — words are relief.",
      "Forgive yourself for yesterday’s mood. It had context.",
    ],
    prompts_es: [
      "Nota en qué fase de tu ciclo estás, sin juicio.",
      "Si es lútea: descansa más fuerte, espera menos.",
      "Si estás ovulando: no abras hoy la puerta cerrada.",
      "Registra una señal del cuerpo (sueño, apetito, ánimo) en tus notas.",
      "Come un snack rico en minerales — nueces, huevo, verduras, chocolate oscuro.",
      "Cancela un plan opcional si tu cuerpo lo pide.",
      "Muévete suave — camina en vez de correr.",
      "Hidrátate, luego magnesio, luego acuéstate más temprano.",
      "Cuéntale a alguien segura en qué fase estás — las palabras alivian.",
      "Perdónate el ánimo de ayer. Tenía contexto.",
    ],
  },
  {
    slug: "healthy-relationships",
    icon: "sparkles",
    label_en: "Healthy Relationships",
    label_es: "Relaciones Sanas",
    desc_en: "Calibrate your sense of safe.",
    desc_es: "Recalibra tu sentido de seguridad.",
    prompts_en: [
      "Name one green flag you saw recently. Out loud counts.",
      "Notice when someone repairs without conditions. That’s rare. That’s real.",
      "Decline a date or hangout that doesn’t excite your body — not just your mind.",
      "Pay attention to how someone treats a server today.",
      "Notice ease, not just chemistry.",
      "Write down what ‘safe’ feels like in your body.",
      "Let someone be slightly inconvenienced by your no. See how they respond.",
      "Spend an hour with someone who doesn’t require performance.",
      "Trust slow. Trust consistent. Trust boring.",
      "Wait a week before believing a big promise. See if it holds.",
    ],
    prompts_es: [
      "Nombra una bandera verde que viste hace poco. Decirla en voz alta cuenta.",
      "Nota cuando alguien repara sin condiciones. Es raro. Es real.",
      "Rechaza una cita que no entusiasme a tu cuerpo — no solo a tu mente.",
      "Observa hoy cómo alguien trata a quien le sirve.",
      "Nota la facilidad, no solo la química.",
      "Anota cómo se siente ‘a salvo’ en tu cuerpo.",
      "Deja que alguien sea un poco incomodado por tu no. Mira cómo responde.",
      "Pasa una hora con alguien que no te pida actuar.",
      "Confía en lo lento. Confía en lo constante. Confía en lo aburrido.",
      "Espera una semana antes de creerte una gran promesa. Mira si se sostiene.",
    ],
  },
];

export function listPaths() { return PATHS; }

export function getPath(slug) {
  return PATHS.find((p) => p.slug === slug) || null;
}

/**
 * Deterministic daily prompt: same date → same prompt for the same path.
 * Uses local-date day-of-year so it rolls over at midnight in the user's tz.
 */
export function dailyPrompt(slug, lang = "en", date = new Date()) {
  const path = getPath(slug);
  if (!path) return null;
  const list = lang === "es" ? path.prompts_es : path.prompts_en;
  if (!Array.isArray(list) || list.length === 0) return null;
  const start = new Date(date.getFullYear(), 0, 0);
  const dayOfYear = Math.floor((date - start) / 86400000);
  return list[dayOfYear % list.length];
}
